import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardMedia,
  CardActions,
  Snackbar,
  Alert,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";

import apiClient, { resolveFileUrl, useApiQuery } from "../api/client";

const STATUS_OPTIONS = ["Completed", "In Progress", "Upcoming"];

const AdminConstructionUpdate = () => {
  const { data, isLoading, refetch } = useApiQuery("/construction-update");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [overallProgress, setOverallProgress] = useState(0);
  const [milestones, setMilestones] = useState([]);
  const [photoLabel, setPhotoLabel] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (data) {
      setOverallProgress(data.overallProgress || 0);
      setMilestones(data.milestones || []);
    }
  }, [data]);

  const addMilestone = () => {
    setMilestones((m) => [...m, { title: "", status: "Upcoming", percent: 0 }]);
  };

  const updateMilestone = (idx, field, value) => {
    setMilestones((m) => m.map((item, i) => (i === idx ? { ...item, [field]: value } : item)));
  };

  const removeMilestone = (idx) => {
    setMilestones((m) => m.filter((_, i) => i !== idx));
  };

  const saveAll = async () => {
    setIsSaving(true);
    try {
      await apiClient.put("/construction-update", { overallProgress, milestones });
      setToast("Construction update saved.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    const formData = new FormData();
    formData.append("file", photoFile);
    formData.append("label", photoLabel);
    setIsUploading(true);
    try {
      await apiClient.post("/construction-update/photos", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoFile(null);
      setPhotoLabel("");
      setToast("Photo added.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to upload photo.");
    } finally {
      setIsUploading(false);
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await apiClient.delete(`/construction-update/photos/${photoId}`);
      setToast("Photo removed.");
      refetch();
    } catch (err) {
      setToast(err?.response?.data?.message || "Failed to remove photo.");
    }
  };

  if (isLoading) return <Typography>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Construction Update
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Overall Progress
        </Typography>
        <TextField
          type="number"
          label="Overall Progress (%)"
          value={overallProgress}
          onChange={(e) => setOverallProgress(Number(e.target.value))}
          inputProps={{ min: 0, max: 100 }}
          sx={{ maxWidth: 240 }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Milestones
          </Typography>
          <Button startIcon={<AddRoundedIcon />} onClick={addMilestone}>
            Add Milestone
          </Button>
        </Box>

        {milestones.map((m, idx) => (
          <Grid container spacing={2} key={idx} sx={{ mb: 1, alignItems: "center" }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Title"
                value={m.title}
                onChange={(e) => updateMilestone(idx, "title", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                select
                label="Status"
                value={m.status}
                onChange={(e) => updateMilestone(idx, "status", e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={4} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Percent"
                value={m.percent}
                onChange={(e) => updateMilestone(idx, "percent", Number(e.target.value))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={2} sm={2}>
              <IconButton color="error" onClick={() => removeMilestone(idx)}>
                <DeleteRoundedIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Divider sx={{ my: 2 }} />
        <Button
          variant="contained"
          startIcon={<SaveRoundedIcon />}
          onClick={saveAll}
          disabled={isSaving}
        >
          Save Changes
        </Button>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Site Photos
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
          <TextField
            label="Label"
            value={photoLabel}
            onChange={(e) => setPhotoLabel(e.target.value)}
          />
          <Button component="label" startIcon={<UploadRoundedIcon />} variant="outlined">
            {photoFile ? photoFile.name : "Choose Photo"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={(e) => setPhotoFile(e.target.files[0])}
            />
          </Button>
          <Button
            variant="contained"
            onClick={uploadPhoto}
            disabled={!photoFile || isUploading}
          >
            {isUploading ? "Uploading…" : "Upload"}
          </Button>
        </Box>

        <Grid container spacing={2}>
          {(data?.photos || []).map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p._id}>
              <Card>
                <CardMedia
                  component="img"
                  height="160"
                  image={resolveFileUrl(p.imageUrl)}
                  alt={p.label}
                />
                <CardActions sx={{ justifyContent: "space-between" }}>
                  <Typography variant="body2">{p.label}</Typography>
                  <IconButton color="error" onClick={() => removePhoto(p._id)}>
                    <DeleteRoundedIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminConstructionUpdate;
